import { makeStyles, shorthands, tokens } from "@fluentui/react-components";
import { ellipsis } from "@/common/style/commonStyle";
import { Colors, ContainerSizeDefaults } from "@/common/constants";
import { MEDIA_QUERIES } from "@/hooks/responsive";

export const useInteractionStyle = makeStyles({
	container: {
		width: "100%",
		height: "calc(100% - 56px)",
		overflow: "auto",
		containerName: "interactionPanel",
		containerType: "inline-size",

		[`@media ${MEDIA_QUERIES.isMobile}`]: {
			height: "calc(100% - 56px - 36px)",
		},
	},
	panelContainer: {
		height: "100%",
	},
	supervisorContainer: {
		width: "100%",
		height: "100%",
		minWidth: "0px",
	},
	waitingAccordion: {
		position: "relative",
	},
	accordionHeader: {
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase200,
		fontStyle: "normal",
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase200,
		textAlign: "center",
		color: tokens.colorNeutralForeground3,
	},
	accordionContainer: {
		overflowY: "auto",
	},
	accordionPanel: {
		margin: "0px",
	},
	accordionCount: {
		fontWeight: tokens.fontWeightBold,
		fontSize: tokens.fontSizeBase200,
		paddingLeft: "8px",
	},
	interactionTimer: {
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase200,
		fontStyle: "normal",
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase200,
	},
	interactionsMenu: {
		padding: "12px 8px",
	},
	interactionIcon: {
		display: "flex",
		justifyContent: "center",
		backgroundColor: "inherit",
		color: "inherit",
		marginTop: "-1px",
	},
	interactionOverlay: {
		cursor: "default",
		pointerEvents: "unset",
	},
	interactionListIcon: {
		color: tokens.colorNeutralForeground3,
		"& svg": {
			color: tokens.colorNeutralForeground3,
		},
	},
	interactionListIconHighlighted: {
		"& svg": {
			color: tokens.colorNeutralForeground3BrandHover,
		},
	},
	activeHeader: {
		fontWeight: tokens.fontWeightBold,
		fontSize: tokens.fontSizeBase300,
		color: tokens.colorNeutralForeground3,
		paddingLeft: "10px",
	},
	waitingHeader: {
		fontWeight: tokens.fontWeightMedium,
		fontSize: tokens.fontSizeBase200,
		color: tokens.colorNeutralForeground3,
	},
	count: {
		paddingLeft: "8px",
	},
	activeBackground: {
		backgroundColor: tokens.colorNeutralBackground1Hover,
	},
	waitingBackground: {
		backgroundColor: tokens.colorNeutralBackground4,
	},
	leftAlign: {
		textAlign: "left",
	},
	activeHeaderContainer: {
		paddingTop: "2px",
		minHeight: "32px",
	},
	waitingHeaderContainer: {
		width: "100%",
	},
	activeInteractionActionsContainer: {
		display: "flex",
		columnGap: "3px",
		justifyContent: "center",
	},
	groupButtonContainer: {
		display: "flex",
		justifyContent: "center",
	},
	bold: {
		fontWeight: "bold",
	},
	topContainer: {
		overflowY: "auto",
		maxHeight: "30vh",
	},
	bottomContainerHeight: {
		overflowY: "auto",
	},
	ellipsis: {
		...ellipsis(),
		">span": {
			...ellipsis(),
			maxWidth: "258px",
		},
	},
	filterFontWeight: {
		fontWeight: 600,
	},
	filterMargin: {
		marginRight: "20px",
	},
	filterSlimMargin: {
		marginRight: "8px",
	},
	filterButton: {
		backgroundColor: tokens.colorNeutralBackground1Selected,
		...shorthands.borderColor(tokens.colorNeutralBackground1Selected),
		fontWeight: tokens.fontWeightRegular,
		color: tokens.colorBrandForegroundOnLight,
		fontSize: tokens.fontSizeBase300,
		minWidth: "0px",
		width: "65px",
		marginLeft: "8px",
		"&:hover, &:hover:active": {
			backgroundColor: tokens.colorNeutralBackground1Selected,
			...shorthands.borderColor(tokens.colorNeutralBackground1Selected),
			fontWeight: tokens.fontWeightRegular,
			color: tokens.colorBrandForegroundOnLight,
			fontSize: tokens.fontSizeBase300,
		},
	},
	filterButtonIcon: {
		width: "20px",
		height: "20px",
		marginLeft: "2px",
	},
	customPaddingButton: {
		paddingLeft: "11px",
		paddingRight: "11px",
		backgroundColor: Colors.ButtonGreenBackground,
		minWidth: "100px",
		"&:hover, &:hover:active": {
			backgroundColor: Colors.ButtonGreenBackground,
		},
	},
	listActionButtonSlim: {
		paddingLeft: "2px",
		paddingRight: "2px",
		minWidth: "32px",
		display: "flex",
		justifyContent: "center",
	},
	pickUpButtonSlim: {
		"& > span": {
			marginRight: "unset",
			marginLeft: "4px",
		},
	},
	listHeaderMedia: {
		[`@container interactionPanel (width < ${ContainerSizeDefaults.InteractionPanelIntermediateWidth}px)`]:
			{
				"& div.additionalContent": {
					display: "none",
				},
				// Tags hide with the queue name they sit under — the rules are kept together so the two
				// cannot drift apart.
				"& div.headerTags": {
					display: "none",
				},
			},
		[`@container interactionPanel (width >= ${ContainerSizeDefaults.InteractionPanelIntermediateWidth}px)`]:
			{
				"& div.additionalContent": {
					maxWidth: "160px",
					...ellipsis(),
				},
				"& div.headerTags": {
					maxWidth: "160px",
				},
				// A tagged row starts its queue column the same distance from the row's end as every other
				// tagged row, however wide its tags and the time beside them, so the stacks line up down
				// the list. Wide enough for a full-width queue column beside the usual timer/status; a
				// longer status widens the row's media instead of squeezing the queue name. Narrower panels
				// reserve a share of their width instead (40% reaches 240px at 600px), so near the
				// breakpoint the name beside the stack keeps its room; every tagged row in the panel still
				// gets the same width, so the stacks still line up.
				"&:has(div.headerTags)": {
					minWidth: "min(240px, 40cqw)",
				},
			},
	},
	// Stacks the queue name and its tags into one column, leaving the timer/status beside them. It fills
	// whatever the header media is wider than its content, which only a tagged row is (see above).
	listHeaderQueueColumn: {
		minWidth: 0,
		flexGrow: 1,
	},
	// The narrow-panel counterpart of "headerTags" above: once the queue column the tags normally sit
	// under is hidden, they reappear as a cluster of colours beside the contact name. The two rules
	// share a breakpoint so exactly one of them is ever visible.
	inlineHeaderTags: {
		display: "none",

		[`@container interactionPanel (width < ${ContainerSizeDefaults.InteractionPanelIntermediateWidth}px)`]:
			{
				display: "flex",
			},
	},
	containsUnreadMessageIcon: {
		position: "relative",
	},
	unreadMessageIcon: {
		height: "7px",
		width: "7px",
		borderRadius: "50%",
		position: "absolute",
		backgroundColor: tokens.colorNeutralForeground2BrandHover,
		top: "-5px",
		left: "17px",
	},
	iconCircle: {
		backgroundColor: tokens.colorNeutralBackground4Hover,
		borderRadius: "50%",
		width: "36px",
		minWidth: "36px",
		height: "36px",
		boxSizing: "border-box",
		flexShrink: 0,
		padding: "1px",
	},
	iconCircleSelected: {
		backgroundColor: tokens.colorBrandBackground,
		borderRadius: "50%",
		"& svg": {
			color: `${tokens.colorNeutralForegroundOnBrand} !important`,
		},
	},
	statusText: {
		fontSize: tokens.fontSizeBase200,
	},
});
